import { type FC, useState, useEffect } from 'react';
import { User, Globe, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { supabase } from '@/lib/api/supabase';

interface SocialLinks {
  instagram?: string;
  facebook?: string;
  youtube?: string;
  twitter?: string;
  linkedin?: string;
}

interface GuestInstructor {
  id: string;
  name: string;
  bio: string;
  photo_url: string | null;
  specialties: string[] | null;
  teaching_focus: string | null;
  social_links: SocialLinks | null;
  website_url: string | null;
  is_featured: boolean | null;
  display_order: number | null;
}

/**
 * Instructors page displaying guest instructors in card format with modal details
 */
export const InstructorsPage: FC = () => {
  const [instructors, setInstructors] = useState<GuestInstructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstructor, setSelectedInstructor] = useState<GuestInstructor | null>(null);

  useEffect(() => {
    const fetchInstructors = async (): Promise<void> => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('guest_instructors')
          .select('*')
          .order('display_order', { ascending: true });

        if (error) {
          console.error('Error fetching instructors:', error);
          return;
        }

        if (data) {
          setInstructors(data as GuestInstructor[]);
        }
      } catch (err) {
        console.error('Failed to fetch instructors:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchInstructors();
  }, []);

  // Get brief description from bio (first sentence or first 150 characters)
  const getBriefDescription = (bio: string): string => {
    const firstSentence = bio.split('.')[0];
    if (firstSentence && firstSentence.length <= 150) {
      return firstSentence + '.';
    }
    return bio.substring(0, 147) + '...';
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
          <p className="mt-4 text-white">Loading instructors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[200px] flex items-center justify-center py-12">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-viking mb-4 drop-shadow-lg">
              Guest Instructors
            </h1>
            <p className="text-lg sm:text-xl drop-shadow-md">
              Learn from world-class HEMA instructors at Cactus Cup
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {instructors.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <User className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <p className="text-white">No instructors available at this time.</p>
              <p className="text-white/70 text-sm mt-2">Check back soon for updates!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {instructors.map((instructor) => (
              <Card
                key={instructor.id}
                className="cursor-pointer hover:border-orange-500/60 transition-all hover:shadow-xl hover:scale-[1.02]"
                onClick={() => setSelectedInstructor(instructor)}
              >
                <CardHeader className="pb-3">
                  {instructor.photo_url ? (
                    <div className="w-full aspect-square rounded-lg overflow-hidden mb-4 bg-turquoise-700/50">
                      <img
                        src={instructor.photo_url}
                        alt={instructor.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-full aspect-square rounded-lg bg-turquoise-700/50 flex items-center justify-center mb-4">
                      <User className="h-16 w-16 text-turquoise-400" />
                    </div>
                  )}
                  <CardTitle className="text-xl break-words line-clamp-2">{instructor.name}</CardTitle>
                  {instructor.is_featured && (
                    <Badge className="w-fit mt-2 bg-orange-500 text-white">Featured</Badge>
                  )}
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-white/80 mb-3 line-clamp-3">
                    {getBriefDescription(instructor.bio)}
                  </p>
                  {instructor.specialties && instructor.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {instructor.specialties.slice(0, 3).map((specialty, idx) => (
                        <Badge
                          key={idx}
                          className="text-xs bg-turquoise-500/40 text-white"
                        >
                          {specialty}
                        </Badge>
                      ))}
                      {instructor.specialties.length > 3 && (
                        <Badge
                          className="text-xs bg-turquoise-500/40 text-white"
                        >
                          +{instructor.specialties.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-orange-400 mt-3">Click to view details</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Instructor Detail Modal */}
      <Dialog open={!!selectedInstructor} onOpenChange={() => setSelectedInstructor(null)}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] flex flex-col bg-turquoise-800 border-2 border-orange-500 text-white p-0">
          {selectedInstructor && (
            <>
              {/* Hero Photo - Full width at top */}
              {selectedInstructor.photo_url && (
                <div className="w-full aspect-[4/3] overflow-hidden flex-shrink-0">
                  <img
                    src={selectedInstructor.photo_url}
                    alt={selectedInstructor.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Content area with padding - scrollable */}
              <div className="p-4 sm:p-6 pb-6 sm:pb-8 flex-1 overflow-y-auto min-h-0 pr-2">
                <div className="space-y-4 sm:space-y-6 pb-2">
                  <DialogHeader className="p-0">
                    <DialogTitle className="text-2xl font-viking text-white pr-6 break-words">
                      {selectedInstructor.name}
                    </DialogTitle>
                    {selectedInstructor.teaching_focus && (
                      <DialogDescription className="text-orange-200 break-words">
                        {selectedInstructor.teaching_focus}
                      </DialogDescription>
                    )}
                  </DialogHeader>

                  {/* Bio */}
                  <div>
                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                      <User className="h-4 w-4 text-orange-400" />
                      About
                    </h3>
                    <div className="text-sm text-orange-200 space-y-2 break-words">
                      {selectedInstructor.bio.split('\n\n').map((paragraph, idx) => (
                        <p key={idx}>{paragraph}</p>
                      ))}
                    </div>
                  </div>

                  {/* Specialties */}
                  {selectedInstructor.specialties && selectedInstructor.specialties.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                        <Award className="h-4 w-4 text-orange-400" />
                        Specialties
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedInstructor.specialties.map((specialty, idx) => (
                          <Badge
                            key={idx}
                            className="bg-orange-500/80 text-white hover:bg-orange-500"
                          >
                            {specialty}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Website and Socials */}
                  {(selectedInstructor.website_url || selectedInstructor.social_links) && (
                    <div>
                      <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                        <Globe className="h-4 w-4 text-orange-400" />
                        Connect
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        {selectedInstructor.website_url && (
                          <a
                            href={selectedInstructor.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-orange-400 hover:text-orange-300 underline"
                          >
                            Website
                          </a>
                        )}
                        {selectedInstructor.social_links?.instagram && (
                          <a
                            href={selectedInstructor.social_links.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-orange-400 hover:text-orange-300 underline"
                          >
                            Instagram
                          </a>
                        )}
                        {selectedInstructor.social_links?.facebook && (
                          <a
                            href={selectedInstructor.social_links.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-orange-400 hover:text-orange-300 underline"
                          >
                            Facebook
                          </a>
                        )}
                        {selectedInstructor.social_links?.youtube && (
                          <a
                            href={selectedInstructor.social_links.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-orange-400 hover:text-orange-300 underline"
                          >
                            YouTube
                          </a>
                        )}
                        {selectedInstructor.social_links?.twitter && (
                          <a
                            href={selectedInstructor.social_links.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-orange-400 hover:text-orange-300 underline"
                          >
                            Twitter
                          </a>
                        )}
                        {selectedInstructor.social_links?.linkedin && (
                          <a
                            href={selectedInstructor.social_links.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-orange-400 hover:text-orange-300 underline"
                          >
                            LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
